/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Entity, EntityActionConfig } from '@/types/Entity';
import { IeKitEditorDialog } from '@/components/iem/kits/IeKitEditorDialog';
import { KitAssignmentDialog } from '@/components/iem/kits/KitAssignmentDialog';
import {
  LocationActionDialogs,
  type LocationActionDialogState,
} from '@/components/locations/LocationActionDialogs';
import {
  CatalogActionDialogs,
  type CatalogActionDialogState,
} from '@/components/catalog/CatalogActionDialogs';
import { logger } from '@equip/utils/logger';
import { PersonSyncModal } from '@/components/person/PersonSyncModal';
import { PersonSizingDialog } from '@/components/iem/dialogs/PersonSizingDialog';
import { PersonBulkSizingImportDialog } from '@/components/iem/dialogs/PersonBulkSizingImportDialog';
import { InactivateIeKitDialog } from '@/components/iem/kits/InactivateIeKitDialog';
import { ReactivateIeKitDialog } from '@/components/iem/kits/ReactivateIeKitDialog';
// Import to ensure registration
import '@/actions/registerCatalogEntityActions';

const log = logger.child({ component: 'EntityActionDialogProvider' });

interface PlaceholderDialogState {
  action: EntityActionConfig;
  entity?: Entity;
}

interface IeKitEditorDialogState {
  action: EntityActionConfig;
  entity?: Entity;
  mode: 'create' | 'edit';
}

interface KitAssignmentDialogState {
  action: EntityActionConfig;
  entity?: Entity;
}

interface PersonSizingDialogState {
  person: Entity;
}

interface InactivateIeKitDialogState {
  action: EntityActionConfig;
  entity?: Entity;
}

export interface EntityActionDialogControls {
  openDialog: <Payload = unknown>(dialogKey: string, payload?: Payload) => void;
  closeDialog: (dialogKey: string) => void;
}

const EntityActionDialogContext = createContext<EntityActionDialogControls | undefined>(undefined);

const getPlaceholderOption = (
  action: EntityActionConfig | undefined,
  key: 'placeholderTitle' | 'placeholderDescription' | 'placeholderCta',
): string | undefined => {
  if (!action?.options) return undefined;
  const value = (action.options as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
};

export const EntityActionDialogProvider = ({ children }: PropsWithChildren) => {
  const [placeholderDialogState, setPlaceholderDialogState] =
    useState<PlaceholderDialogState | null>(null);
  const [ieKitEditorState, setIeKitEditorState] = useState<IeKitEditorDialogState | null>(null);
  const [kitAssignmentDialogState, setKitAssignmentDialogState] =
    useState<KitAssignmentDialogState | null>(null);
  const [locationDialogState, setLocationDialogState] = useState<LocationActionDialogState | null>(
    null,
  );
  const [catalogDialogState, setCatalogDialogState] = useState<CatalogActionDialogState | null>(
    null,
  );
  const [personSyncModalOpen, setPersonSyncModalOpen] = useState(false);
  const [personSizingDialogState, setPersonSizingDialogState] =
    useState<PersonSizingDialogState | null>(null);
  const [personBulkSizingImportOpen, setPersonBulkSizingImportOpen] = useState(false);
  const [inactivateIeKitState, setInactivateIeKitState] =
    useState<InactivateIeKitDialogState | null>(null);
  const [reactivateIeKitState, setReactivateIeKitState] =
    useState<InactivateIeKitDialogState | null>(null);

  const openDialog = useCallback<EntityActionDialogControls['openDialog']>((dialogKey, payload) => {
    switch (dialogKey) {
      case 'placeholder':
        setPlaceholderDialogState(payload as PlaceholderDialogState);
        return;
      case 'ieKitEditor':
        setIeKitEditorState(payload as IeKitEditorDialogState);
        return;
      case 'kitAssignments':
        setKitAssignmentDialogState(payload as KitAssignmentDialogState);
        return;
      case 'location.actions':
        setLocationDialogState(payload as LocationActionDialogState);
        return;
      case 'catalog.actions':
        setCatalogDialogState(payload as CatalogActionDialogState);
        return;
      case 'person.sync':
        setPersonSyncModalOpen(true);
        return;
      case 'person.manageSizing':
        setPersonSizingDialogState(payload as PersonSizingDialogState);
        return;
      case 'person.bulkSizingImport':
        setPersonBulkSizingImportOpen(true);
        return;
      case 'ieKit.inactivate':
        setInactivateIeKitState(payload as InactivateIeKitDialogState);
        return;
      case 'ieKit.reactivate':
        setReactivateIeKitState(payload as InactivateIeKitDialogState);
        return;
      default:
        if (import.meta.env.DEV) {
          log.warn('Unknown dialog key on open', { dialogKey });
        }
    }
  }, []);

  const closeDialog = useCallback<EntityActionDialogControls['closeDialog']>((dialogKey) => {
    switch (dialogKey) {
      case 'placeholder':
        setPlaceholderDialogState(null);
        return;
      case 'ieKitEditor':
        setIeKitEditorState(null);
        return;
      case 'kitAssignments':
        setKitAssignmentDialogState(null);
        return;
      case 'location.actions':
        setLocationDialogState(null);
        return;
      case 'catalog.actions':
        setCatalogDialogState(null);
        return;
      case 'person.sync':
        setPersonSyncModalOpen(false);
        return;
      case 'person.manageSizing':
        setPersonSizingDialogState(null);
        return;
      case 'person.bulkSizingImport':
        setPersonBulkSizingImportOpen(false);
        return;
      case 'ieKit.inactivate':
        setInactivateIeKitState(null);
        return;
      case 'ieKit.reactivate':
        setReactivateIeKitState(null);
        return;
      default:
        if (import.meta.env.DEV) {
          log.warn('Unknown dialog key on close', { dialogKey });
        }
    }
  }, []);

  const contextValue = useMemo<EntityActionDialogControls>(
    () => ({
      openDialog,
      closeDialog,
    }),
    [openDialog, closeDialog],
  );

  const placeholderTitle =
    getPlaceholderOption(placeholderDialogState?.action, 'placeholderTitle') ??
    (placeholderDialogState ? `${placeholderDialogState.action.label} Placeholder` : 'Action');

  const placeholderDescription =
    getPlaceholderOption(placeholderDialogState?.action, 'placeholderDescription') ??
    (placeholderDialogState
      ? (() => {
          if (placeholderDialogState.entity) {
            const entityName =
              placeholderDialogState.entity.display_name ??
              placeholderDialogState.entity.entity_identifier;
            return `Future ${placeholderDialogState.action.label.toLowerCase()} workflow for "${entityName}" will appear here.`;
          }
          return `Future ${placeholderDialogState.action.label.toLowerCase()} workflow will appear here.`;
        })()
      : 'Future action workflow will appear here.');

  const placeholderCta =
    getPlaceholderOption(placeholderDialogState?.action, 'placeholderCta') ?? 'Return to list';

  return (
    <EntityActionDialogContext.Provider value={contextValue}>
      {children}
      <IeKitEditorDialog
        open={Boolean(ieKitEditorState)}
        mode={ieKitEditorState?.mode ?? 'create'}
        kit={ieKitEditorState?.entity ?? null}
        onClose={() => closeDialog('ieKitEditor')}
      />
      <KitAssignmentDialog
        open={Boolean(kitAssignmentDialogState)}
        kit={kitAssignmentDialogState?.entity ?? null}
        onClose={() => closeDialog('kitAssignments')}
      />
      <LocationActionDialogs
        state={locationDialogState}
        onClose={() => closeDialog('location.actions')}
      />
      <CatalogActionDialogs
        state={catalogDialogState}
        onClose={() => closeDialog('catalog.actions')}
      />
      <PersonSyncModal
        open={personSyncModalOpen}
        onClose={() => closeDialog('person.sync')}
        onComplete={() => {
          // Optionally refresh the person list or show notification
        }}
      />
      <PersonSizingDialog
        open={Boolean(personSizingDialogState)}
        person={personSizingDialogState?.person}
        onClose={() => closeDialog('person.manageSizing')}
      />
      <PersonBulkSizingImportDialog
        open={personBulkSizingImportOpen}
        onClose={() => closeDialog('person.bulkSizingImport')}
      />
      <InactivateIeKitDialog
        open={Boolean(inactivateIeKitState)}
        kit={inactivateIeKitState?.entity ?? null}
        onClose={() => closeDialog('ieKit.inactivate')}
      />
      <ReactivateIeKitDialog
        open={Boolean(reactivateIeKitState)}
        kit={reactivateIeKitState?.entity ?? null}
        onClose={() => closeDialog('ieKit.reactivate')}
      />
      <Dialog
        fullScreen
        open={Boolean(placeholderDialogState)}
        onClose={() => closeDialog('placeholder')}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => closeDialog('placeholder')}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {placeholderTitle}
            </Typography>
            <Button color="inherit" onClick={() => closeDialog('placeholder')}>
              Close
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: 2,
            py: 8,
          }}
        >
          <Typography variant="h4" component="h1" sx={{ textTransform: 'capitalize' }}>
            {placeholderDialogState?.action.label ?? 'Action'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
            {placeholderDescription}
          </Typography>
          <Button variant="contained" onClick={() => closeDialog('placeholder')}>
            {placeholderCta}
          </Button>
        </DialogContent>
      </Dialog>
    </EntityActionDialogContext.Provider>
  );
};

export const useEntityActionDialogs = (): EntityActionDialogControls => {
  const context = useContext(EntityActionDialogContext);
  if (!context) {
    throw new Error('useEntityActionDialogs must be used within an EntityActionDialogProvider.');
  }
  return context;
};
